// components/HazardChecklist.jsx - Expanded hazard checklist with inline mitigations
import React, { useState, useEffect, useRef } from 'react';

const HAZARD_CATEGORIES = {
  weather: {
    label: 'Weather Hazards',
    icon: '🌤️',
    collapsible: true,
    alwaysExpanded: false,
    items: [
      { id: 'heat', label: 'Extreme Heat', requiresMitigation: true, autoDetect: true },
      { id: 'cold', label: 'Extreme Cold', requiresMitigation: true, autoDetect: true },
      { id: 'wind', label: 'High Winds', requiresMitigation: true, autoDetect: true },
      { id: 'rain', label: 'Rain/Wet Conditions', requiresMitigation: true, autoDetect: true },
      { id: 'lightning', label: 'Lightning Risk', requiresMitigation: true, autoDetect: true },
      { id: 'ice', label: 'Ice/Snow', requiresMitigation: true, autoDetect: true }
    ]
  },
  workZone: {
    label: 'Work Zone Hazards',
    icon: '🚧',
    alwaysExpanded: true,
    items: [
      { id: 'slips_trips', label: 'Slips, Trips, Falls', requiresMitigation: true },
      { id: 'traffic', label: 'Traffic', requiresMitigation: true },
      { id: 'targets', label: 'Targets', requiresMitigation: true },
      { id: 'pedestrians', label: 'Pedestrians', requiresMitigation: true },
      { id: 'poor_visibility', label: 'Poor Visibility', requiresMitigation: true },
      { id: 'water', label: 'Water', requiresMitigation: true },
      { id: 'steep_slope', label: 'Steep Slope', requiresMitigation: true },
      { id: 'workzone_other', label: 'Other', requiresMitigation: true }
    ]
  },
  tree: {
    label: 'Tree Hazards',
    icon: '🌳',
    collapsible: true,
    requiresInspection: true,
    inspectionCheckboxLabel: 'Visual tree inspection performed?',
    items: [
      { id: 'large_tree', label: 'Large Tree/Fatigue Hazard', requiresMitigation: true },
      { id: 'rotted_base', label: 'Rotted Base/Roots', requiresMitigation: true },
      { id: 'mushrooms', label: 'Mushrooms on Trunk', requiresMitigation: true },
      { id: 'cavities', label: 'Cavities/Hollows', requiresMitigation: true },
      { id: 'cracked_trunk', label: 'Cracked Trunk', requiresMitigation: true },
      { id: 'very_dead', label: 'Very Dead (Visible Loose Bark)', requiresMitigation: true },
      { id: 'bees_animals', label: 'Bees/Animals in Tree', requiresMitigation: true },
      { id: 'severe_lean', label: 'Severe Lean', requiresMitigation: true },
      { id: 'species_concerns', label: 'Species Specific Concerns', requiresMitigation: true },
      { id: 'tree_other', label: 'Other', requiresMitigation: true }
    ]
  },
  equipment: {
    label: 'Equipment',
    icon: '⚙️',
    alwaysExpanded: true,
    helpText: 'Ensure all necessary guards are in place and all operators have needed training and PPE before using.',
    items: [
      { id: 'chipper', label: 'Chipper', requiresMitigation: true },
      { id: 'chainsaw', label: 'Chainsaw', requiresMitigation: true },
      { id: 'lift_crane', label: 'Lift or Crane', requiresMitigation: true },
      { id: 'loader_excavator', label: 'Loader/Skidsteer/Excavator', requiresMitigation: true },
      { id: 'stump_grinder', label: 'Stump Grinder', requiresMitigation: true },
      { id: 'equipment_other', label: 'Other', requiresMitigation: true }
    ]
  },
  electrical: {
    label: 'Electrical',
    icon: '⚡',
    collapsible: true,
    requiresInspection: true,
    inspectionCheckboxLabel: 'Visual electrical inspection performed?',
    items: [
      { id: 'overhead_wires', label: 'Overhead Wires', requiresMitigation: true },
      { id: 'electrical_boxes', label: 'Electrical Boxes', requiresMitigation: true },
      { id: 'electrical_other', label: 'Other', requiresMitigation: true }
    ]
  },
  underground: {
    label: 'Underground Hazards',
    icon: '',
    alwaysExpanded: true,
    requiresLocates: true,
    locatesHelpText: 'Before digging, underground utilities shall be marked by utility-locating services. Call 811 or use local utility-locating services.',
    items: [
      { id: 'soft_ground', label: 'Soft/Sensitive Ground', requiresMitigation: true },
      { id: 'septic', label: 'Septic Tank or Drainfield', requiresMitigation: true },
      { id: 'underground_other', label: 'Other', requiresMitigation: true }
    ]
  },
  unfamiliar: {
    label: 'Unfamiliar Conditions',
    icon: '',
    alwaysExpanded: true,
    items: [
      { id: 'unfamiliar_tools', label: 'Tools, Equipment, or Systems', requiresMitigation: true },
      { id: 'unfamiliar_personnel', label: 'Personnel', requiresMitigation: true },
      { id: 'unfamiliar_species', label: 'Species', requiresMitigation: true },
      { id: 'unfamiliar_other', label: 'Other', requiresMitigation: true }
    ]
  }
};

// Equipment mitigation prefills
const EQUIPMENT_MITIGATIONS = {
  chipper: 'Risk: Flying debris, entanglement, crush injuries. PPE: Safety glasses, hearing protection, gloves, steel-toe boots, high-vis vest.',
  chainsaw: 'Risk: Lacerations, kickback, falling limbs. PPE: Chainsaw chaps, helmet with face shield, hearing protection, gloves, steel-toe boots.',
  lift_crane: 'Risk: Tip-over, electrocution, falls. PPE: Hard hat, harness when in basket, high-vis vest. Certified operator required.',
  loader_excavator: 'Risk: Crush injuries, tip-over, blind spots. PPE: Hard hat, steel-toe boots, high-vis vest. Certified operator required.',
  stump_grinder: 'Risk: Flying debris, entanglement, crush injuries. PPE: Safety glasses, face shield, hearing protection, gloves, steel-toe boots.'
};

// Weather hazard guidance (shown in help text, not prefilled)
const WEATHER_HAZARD_GUIDANCE = {
  heat: 'Consider: Frequent hydration breaks, shade availability, monitoring for heat exhaustion signs, adjust work schedule.',
  cold: 'Consider: Layer clothing, warm-up breaks, monitoring for frostbite, hand/foot warmers.',
  wind: 'Consider: Delay climbing if gusts exceed 25mph, secure all equipment, monitor forecasts.',
  rain: 'Consider: Extra caution on wet surfaces, evaluate if conditions require postponement.',
  lightning: 'Consider: Suspend all operations immediately, seek shelter, monitor weather alerts.',
  ice: 'Consider: Postpone if icy conditions present, use traction devices if necessary, evaluate surface conditions.'
};

const HazardChecklist = ({ data, onChange, weather }) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [inspections, setInspections] = useState({
    tree: false,
    electrical: false
  });

  // Refs for auto-focus
  const mitigationRefs = useRef({});

  const hazards = data || {
    identified: {},
    mitigations: {},
    inspections: {},
    additionalNotes: ''
  };

  // Auto-detect weather hazards
  useEffect(() => {
    if (weather?.hazards && weather.hazards.length > 0) {
      const newIdentified = { ...hazards.identified };
      let hasChanges = false;

      weather.hazards.forEach(hazard => {
        const hazardId = hazard.type;
        if (!newIdentified[hazardId]) {
          newIdentified[hazardId] = {
            present: true,
            autoDetected: true,
            severity: hazard.severity
          };
          hasChanges = true;
        }
      });

      if (hasChanges) {
        // Auto-expand weather section
        setExpandedSections(prev => ({ ...prev, weather: true }));
        updateHazards({ identified: newIdentified });
      }
    }
  }, [weather]);

  const updateHazards = (updates) => {
    onChange({ ...hazards, ...updates });
  };

  const toggleHazard = (hazardId, requiresMitigation, categoryId) => {
    const newIdentified = { ...hazards.identified };
    const newMitigations = { ...hazards.mitigations };

    if (newIdentified[hazardId]) {
      // Remove hazard
      delete newIdentified[hazardId];
      delete newMitigations[hazardId];
    } else {
      // Add hazard
      newIdentified[hazardId] = { present: true };
      
      // Prefill equipment mitigations
      if (categoryId === 'equipment' && EQUIPMENT_MITIGATIONS[hazardId]) {
        newMitigations[hazardId] = EQUIPMENT_MITIGATIONS[hazardId];
      }
      
      // Auto-focus the mitigation field after a short delay
      setTimeout(() => {
        if (mitigationRefs.current[hazardId]) {
          mitigationRefs.current[hazardId].focus();
        }
      }, 100);
    }

    updateHazards({
      identified: newIdentified,
      mitigations: newMitigations
    });
  };

  const updateMitigation = (hazardId, text) => {
    updateHazards({
      mitigations: {
        ...hazards.mitigations,
        [hazardId]: text
      }
    });
  };

  const handleInspectionCheck = (categoryId, checked) => {
    setInspections(prev => ({ ...prev, [categoryId]: checked }));
    if (checked) {
      setExpandedSections(prev => ({ ...prev, [categoryId]: true }));
    }
    
    updateHazards({
      inspections: {
        ...hazards.inspections,
        [categoryId]: checked
      }
    });
  };

  const countCategoryHazards = (categoryId) => {
    const category = HAZARD_CATEGORIES[categoryId];
    return category.items.filter(item => hazards.identified[item.id]).length;
  };

  const totalHazards = Object.keys(hazards.identified).length;

  // Build weather guidance text from detected hazards
  const weatherGuidanceText = weather?.hazards?.length > 0 ? (
    <div style={{ fontSize: '13px', color: '#233D48', marginTop: '8px' }}>
      <strong style={{ color: '#032B3A' }}>Mitigation guidance:</strong>
      <ul style={{ margin: '6px 0 0 0', paddingLeft: '20px' }}>
        {weather.hazards.map(h => (
          <li key={h.type} style={{ marginTop: '4px' }}>
            {WEATHER_HAZARD_GUIDANCE[h.type]}
          </li>
        ))}
      </ul>
    </div>
  ) : null;

  return (
    <div style={{ marginBottom: '24px' }}>
      <h2 style={{ 
        fontSize: '18px', 
        fontWeight: '600', 
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        ⚠️ Jobsite Hazards
        {totalHazards > 0 && (
          <span style={{
            backgroundColor: '#F5F0D1',
            color: '#032B3A',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '14px',
            border: '1px solid #CDB52D'
          }}>
            {totalHazards} identified
          </span>
        )}
      </h2>

      {/* Weather auto-detection notice with guidance */}
      {weather?.hazards?.length > 0 && (
        <div style={{
          backgroundColor: '#F5F0D1',
          borderLeft: '4px solid #CDB52D',
          borderRadius: '0 8px 8px 0',
          padding: '12px 16px',
          marginBottom: '16px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: '10px'
          }}>
            <span style={{ fontSize: '20px' }}>🌤️</span>
            <div style={{ flex: 1 }}>
              <strong style={{ color: '#032B3A' }}>Weather hazards auto-detected:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#032B3A' }}>
                {weather.hazards.map(h => (
                  <li key={h.type}>{h.message}</li>
                ))}
              </ul>
              {weatherGuidanceText}
            </div>
          </div>
        </div>
      )}

      {/* Hazard Categories */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {Object.entries(HAZARD_CATEGORIES).map(([categoryId, category]) => {
          const isCollapsible = category.collapsible || category.requiresInspection;
          const isExpanded = category.alwaysExpanded || expandedSections[categoryId];
          const hazardCount = countCategoryHazards(categoryId);
          const inspectionRequired = category.requiresInspection;
          const inspectionCompleted = inspections[categoryId];
          
          return (
            <div
              key={categoryId}
              style={{
                backgroundColor: '#F1F0E9',
                borderRadius: '8px',
                overflow: 'hidden',
                border: hazardCount > 0 ? '1px solid #CDB52D' : '1px solid #DADFE2'
              }}
            >
              {/* Category Header */}
              {(isCollapsible && !inspectionRequired) && (
                <button
                  onClick={() => setExpandedSections(prev => ({ 
                    ...prev, 
                    [categoryId]: !prev[categoryId] 
                  }))}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {category.icon && <span style={{ fontSize: '20px' }}>{category.icon}</span>}
                    <span style={{ fontWeight: '500' }}>{category.label}</span>
                    {hazardCount > 0 && (
                      <span style={{
                        backgroundColor: '#F5F0D1',
                        color: '#032B3A',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: '600',
                        border: '1px solid #CDB52D'
                      }}>
                        {hazardCount}
                      </span>
                    )}
                  </span>
                  <span style={{
                    transform: isExpanded ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                    fontSize: '12px',
                    color: '#5D757E'
                  }}>
                    ▼
                  </span>
                </button>
              )}

              {/* Non-collapsible header */}
              {!isCollapsible && !inspectionRequired && (
                <div style={{
                  padding: '14px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  borderBottom: '1px solid #DADFE2'
                }}>
                  {category.icon && <span style={{ fontSize: '20px' }}>{category.icon}</span>}
                  <span style={{ fontWeight: '500' }}>{category.label}</span>
                  {hazardCount > 0 && (
                    <span style={{
                      backgroundColor: '#F5F0D1',
                      color: '#032B3A',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '12px',
                      fontWeight: '600',
                      border: '1px solid #CDB52D'
                    }}>
                      {hazardCount}
                    </span>
                  )}
                </div>
              )}

              {/* Inspection checkbox header */}
              {inspectionRequired && (
                <>
                  <div style={{
                    padding: '14px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    borderBottom: '1px solid #DADFE2'
                  }}>
                    {category.icon && <span style={{ fontSize: '20px' }}>{category.icon}</span>}
                    <span style={{ fontWeight: '500' }}>{category.label}</span>
                    {hazardCount > 0 && (
                      <span style={{
                        backgroundColor: '#F5F0D1',
                        color: '#032B3A',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: '600',
                        border: '1px solid #CDB52D'
                      }}>
                        {hazardCount}
                      </span>
                    )}
                  </div>
                  <div style={{
                    padding: '14px 16px',
                    backgroundColor: inspectionCompleted ? '#E7F0E5' : '#FAECEA',
                    borderLeft: `4px solid ${inspectionCompleted ? '#388523' : '#D24232'}`,
                    borderBottom: '1px solid #DADFE2'
                  }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '500'
                    }}>
                      <input
                        type="checkbox"
                        checked={inspectionCompleted}
                        onChange={(e) => handleInspectionCheck(categoryId, e.target.checked)}
                        style={{
                          width: '20px',
                          height: '20px',
                          cursor: 'pointer',
                          accentColor: '#388523'
                        }}
                      />
                      <span style={{ color: '#032B3A' }}>
                        {category.inspectionCheckboxLabel}
                        {!inspectionCompleted && (
                          <span style={{ color: '#D24232', marginLeft: '4px' }}>*</span>
                        )}
                        {inspectionCompleted && (
                          <span style={{ color: '#388523', marginLeft: '8px' }}>✓</span>
                        )}
                      </span>
                    </label>
                  </div>
                </>
              )}

              {/* Help text */}
              {category.helpText && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#E2F1FD',
                  borderLeft: '4px solid #3B9AE8',
                  fontSize: '13px',
                  color: '#032B3A',
                  borderBottom: '1px solid #DADFE2'
                }}>
                  💡 {category.helpText}
                </div>
              )}

              {/* Underground Locates Requirement */}
              {category.requiresLocates && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: hazards.undergroundLocates ? '#ffffff' : '#FAECEA',
                  borderLeft: `4px solid ${hazards.undergroundLocates === 'completed' ? '#388523' : (hazards.undergroundLocates ? '#CDB52D' : '#D24232')}`,
                  borderBottom: '1px solid #DADFE2'
                }}>
                  <div style={{
                    fontSize: '13px',
                    color: '#032B3A',
                    marginBottom: '10px'
                  }}>
                    Before digging, underground utilities shall be marked by utility-locating services. Call 811 or use local utility-locating services.
                  </div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#032B3A',
                    marginBottom: '8px'
                  }}>
                    Utility Locates Status
                    {!hazards.undergroundLocates && <span style={{ color: '#D24232', marginLeft: '4px' }}>*</span>}
                  </label>
                  <div style={{
                    display: 'flex',
                    gap: '8px'
                  }}>
                    {[
                      { value: 'completed', label: '✓ Completed', color: '#388523' },
                      { value: 'not_needed', label: 'Not Needed', color: '#5D757E' },
                      { value: 'pending', label: 'Pending', color: '#CDB52D' }
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => updateHazards({ undergroundLocates: option.value })}
                        style={{
                          flex: 1,
                          padding: '10px 8px',
                          backgroundColor: hazards.undergroundLocates === option.value ? option.color : 'white',
                          color: hazards.undergroundLocates === option.value ? 'white' : '#233D48',
                          border: hazards.undergroundLocates === option.value ? 'none' : '1px solid #DADFE2',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: hazards.undergroundLocates === option.value ? '600' : '400'
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  {!hazards.undergroundLocates && (
                    <div style={{
                      marginTop: '8px',
                      fontSize: '12px',
                      color: '#D24232',
                      fontWeight: '500'
                    }}>
                      ⚠ Selection required
                    </div>
                  )}
                </div>
              )}

              {/* Category Items - only show if expanded or always expanded */}
              {(isExpanded || (!isCollapsible && !inspectionRequired)) && (
                <div style={{ padding: '16px' }}>
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    {category.items.map(item => {
                      const isSelected = !!hazards.identified[item.id];
                      const isAutoDetected = hazards.identified[item.id]?.autoDetected;
                      const hasMitigation = hazards.mitigations[item.id]?.trim();
                      
                      return (
                        <div 
                          key={item.id} 
                          style={{ 
                            backgroundColor: '#ffffff',
                            border: '1px solid #DADFE2',
                            borderRadius: '8px',
                            overflow: 'hidden'
                          }}
                        >
                          {/* Hazard toggle button */}
                          <button
                            onClick={() => toggleHazard(item.id, item.requiresMitigation, categoryId)}
                            style={{
                              width: '100%',
                              padding: '14px 16px',
                              backgroundColor: isSelected ? '#F5F0D1' : '#ffffff',
                              borderLeft: isSelected ? '4px solid #CDB52D' : 'none',
                              border: 'none',
                              borderBottom: isSelected && item.requiresMitigation ? '1px solid #DADFE2' : 'none',
                              cursor: 'pointer',
                              textAlign: 'left',
                              fontSize: '15px',
                              color: '#032B3A',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              boxSizing: 'border-box'
                            }}
                          >
                            <span style={{ fontWeight: isSelected ? '500' : '400' }}>
                              {item.label}
                              {isAutoDetected && (
                                <span style={{ 
                                  marginLeft: '8px',
                                  fontSize: '12px',
                                  color: '#CDB52D'
                                }}>
                                  (auto-detected)
                                </span>
                              )}
                            </span>
                            {isSelected && (
                              <span style={{ 
                                color: hasMitigation ? '#388523' : '#5D757E',
                                fontSize: '14px'
                              }}>
                                {hasMitigation ? '✓' : '−'}
                              </span>
                            )}
                          </button>
                          
                          {/* Mitigation area */}
                          {isSelected && item.requiresMitigation && (
                            <div style={{ padding: '16px', backgroundColor: '#ffffff' }}>
                              <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#032B3A',
                                marginBottom: '8px'
                              }}>
                                Mitigation Plan <span style={{ color: '#D24232' }}>*</span>
                              </label>
                              <textarea
                                ref={el => mitigationRefs.current[item.id] = el}
                                value={hazards.mitigations[item.id] || ''}
                                onChange={(e) => updateMitigation(item.id, e.target.value)}
                                placeholder="Describe existing hazard potential and mitigation measures..."
                                rows={3}
                                style={{
                                  width: '100%',
                                  padding: '12px',
                                  paddingLeft: '16px',
                                  border: '1px solid #DADFE2',
                                  borderLeft: `4px solid ${hasMitigation ? '#388523' : '#D24232'}`,
                                  borderRadius: '6px',
                                  fontSize: '15px',
                                  resize: 'vertical',
                                  boxSizing: 'border-box',
                                  backgroundColor: '#ffffff'
                                }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Additional Notes */}
      <div style={{
        backgroundColor: '#F1F0E9',
        borderRadius: '8px',
        padding: '16px',
        marginTop: '16px'
      }}>
        <label style={{ fontWeight: '500', display: 'block', marginBottom: '8px' }}>
          Additional Safety Notes
        </label>
        <textarea
          value={hazards.additionalNotes || ''}
          onChange={(e) => updateHazards({ additionalNotes: e.target.value })}
          placeholder="Any other safety concerns or notes..."
          rows={3}
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #DADFE2',
            borderRadius: '6px',
            fontSize: '14px',
            resize: 'vertical',
            boxSizing: 'border-box'
          }}
        />
      </div>
    </div>
  );
};

export default HazardChecklist;